import { ActivityParentEntityId, TaskTypeId } from "@sp-crm/core";
import { PrimaryButton } from "components/ui/primary-button";
import { SecondaryButton } from "components/ui/secondary-button";
import { useCreateTaskMutation, useGetTaskTypesQuery } from "generated/graphql";
import React from "react";
import { useCurrentUserId } from "store/selectors/hooks";
import { stableQueryOptions } from "util/requests";

interface LogActivityFormProps {
    entityId: ActivityParentEntityId;
    onComplete: () => void;
    onCancel: () => void;
}

export const LogActivityForm: React.FC<LogActivityFormProps> = props => {
    const { entityId, onComplete, onCancel } = props;
    const currentUserId = useCurrentUserId();
    const taskTypesQuery = useGetTaskTypesQuery({}, stableQueryOptions());
    const createTask = useCreateTaskMutation();

    const [taskTypeId, setTaskTypeId] = React.useState<TaskTypeId | "">("");
    const [text, setText] = React.useState("");

    const activeTaskTypes = React.useMemo(() => {
        if (!taskTypesQuery.isSuccess) return [];
        return taskTypesQuery.data.getTaskTypes
            .filter(t => !t.isArchived)
            .sort((a, b) =>
                a.name.toLocaleLowerCase().localeCompare(b.name.toLocaleLowerCase()),
            );
    }, [taskTypesQuery.isSuccess, taskTypesQuery.data?.getTaskTypes]);

    React.useEffect(() => {
        if (activeTaskTypes.length > 0 && !taskTypeId) {
            setTaskTypeId(activeTaskTypes[0].id);
        }
    }, [activeTaskTypes, taskTypeId]);

    const handleSubmit = React.useCallback(
        async (e: React.FormEvent) => {
            e.preventDefault();
            if (!text.trim()) return;

            await createTask.mutateAsync({
                parentId: entityId,
                params: {
                    text: text.trim(),
                    taskTypeId: taskTypeId || undefined,
                    status: "done",
                    completedAt: new Date().toISOString(),
                    ownerId: currentUserId,
                },
            });
            onComplete();
        },
        [text, taskTypeId, entityId, currentUserId, createTask, onComplete],
    );

    return (
        <form
            onSubmit={handleSubmit}
            className="flex items-end gap-2 py-2 border-b border-gray-200"
            data-testid="log-activity-form">
            <div className="flex-shrink-0">
                <label className="block mb-1 text-sm font-medium text-gray-700">
                    Type
                </label>
                <select
                    className="form-select rounded-sm md:rounded border-gray-300 hover:border-brand-600 text-sm"
                    value={taskTypeId}
                    onChange={e => setTaskTypeId(e.target.value as TaskTypeId)}>
                    {activeTaskTypes.map(t => (
                        <option key={t.id} value={t.id}>
                            {t.name}
                        </option>
                    ))}
                </select>
            </div>
            <div className="flex-grow">
                <label className="block mb-1 text-sm font-medium text-gray-700">
                    Activity
                </label>
                <input
                    type="text"
                    className="form-input rounded-sm md:rounded w-full border-gray-300 hover:border-brand-600 text-sm"
                    placeholder="What happened?"
                    value={text}
                    onChange={e => setText(e.target.value)}
                    autoFocus
                />
            </div>
            <PrimaryButton
                type="submit"
                disabled={!text.trim() || createTask.isLoading}
                className="text-sm">
                Log
            </PrimaryButton>
            <SecondaryButton type="button" onClick={onCancel} className="text-sm">
                Cancel
            </SecondaryButton>
        </form>
    );
};
